<template>
  <v-container @drop.prevent="addFile" @dragover.prevent>
    <GlobalUiAlert class="mb-2" />
    <v-layout class="mb-2" align-center>
      <h1>{{ $t('documents.title') }}</h1>
      <v-spacer />

      <v-menu
        transition="slide-y-transition"
        bottom
        right
        class="my-0"
        v-if="!isEmpty(selectedDocuments)"
      >
        <soraban-action-button
          icon="more_vert"
          help="Actions for Selected"
          slot="activator"
        />
        <v-list class="caption">
          <v-list-tile @click="$emit('openMoveMultipleDocumentsDialog', true)">
            <v-list-tile-title>
              Move Selected ({{ selectedDocuments.length }})
            </v-list-tile-title>
          </v-list-tile>
          <v-list-tile @click="$emit('openMoveMultipleDocumentsDialog', false)">
            <v-list-tile-title>
              Copy Selected ({{ selectedDocuments.length }})
            </v-list-tile-title>
          </v-list-tile>
          <v-list-tile @click="$emit('openDeleteMultipleConfirmationDialog')">
            <v-list-tile-title>
              Delete Selected ({{ selectedDocuments.length }})
            </v-list-tile-title>
          </v-list-tile>
          <v-list-tile
            @click="$emit('openDownloadMultipleDialog')"
            v-if="!isEmpty(selectedDocuments)"
          >
            <v-list-tile-title>
              Download Selected ({{ selectedDocuments.length }})
            </v-list-tile-title>
          </v-list-tile>
          <v-list-tile
            v-if="!hasFolder"
            @click="$emit('openAttachToQuestionnaireDialog', selectedDocuments)"
          >
            <v-list-tile-title>
              Attach Selected ({{ selectedDocuments.length }}) To Questionnaire
            </v-list-tile-title>
          </v-list-tile>
          <v-list-tile
            v-if="!hasFolder && allAttached"
            @click="$emit('detachFromQuestionnaire', selectedDocuments)"
          >
            <v-list-tile-title>
              Detach Selected ({{ selectedDocuments.length }})
            </v-list-tile-title>
          </v-list-tile>
        </v-list>
      </v-menu>

      <soraban-action-button
        @click="$emit('openActivityLogDialog')"
        icon="schedule"
        help="History"
        v-if="isAdvisor"
      />
      <soraban-action-button
        @click="$emit('openImportDocumentsDialog')"
        icon="upload_file"
        help="Upload Documents"
        v-if="isAdvisor && isEmpty(searchingTerm)"
      />
      <soraban-action-button
        @click="$emit('openCreateFolderDialog')"
        icon="create_new_folder"
        help="Create Folder"
        v-if="isAdvisor && isEmpty(searchingTerm)"
      />
    </v-layout>

    <v-layout align-center justify-space-between>
      <v-layout row align-center v-if="!isEmpty(searchingTerm)">
        <h3
          class="grey--text text--darken-3 font-weight-regular"
          style="font-size: 16px"
        >
          Search results for <b>{{ searchingTerm }}</b
          >:
        </h3>
        <v-btn
          class="primary--text text-none"
          small
          outline
          @click="clearSearch()"
          >Clear Search</v-btn
        >
      </v-layout>
      <v-breadcrumbs v-else :items="paths" class="breadcrumbs">
        <template v-slot:item="props">
          <span v-if="props.item.disabled">
            {{ isEmpty(props.item.text) ? 'Home' : props.item.text }}
          </span>
          <span
            class="clickable"
            v-else
            @click="$emit('goToPath', props.item.path)"
          >
            {{ isEmpty(props.item.text) ? 'Home' : props.item.text }}
          </span>
        </template>
      </v-breadcrumbs>

      <v-flex xs4 class="hidden-sm-and-down">
        <v-text-field
          ref="searchInput"
          :value="searchTerm"
          @input="$emit('update:searchTerm', $event)"
          append-icon="search"
          label="Search"
          single-line
          placeholder="Search"
          hide-details
          solo
          flat
          class="search-input"
          @keyup.native.enter="$refs.searchInput.blur()"
          @blur="$emit('searchDocuments')"
        />
      </v-flex>
    </v-layout>

    <v-layout column class="py-3">
      <v-data-table
        :headers="documentHeaders"
        :items="documents"
        hide-actions
        disable-initial-sort
        :loading="loading"
        :custom-filter="customDocumentFilter"
        class="documents-table"
        :select-all="$vuetify.breakpoint.mdAndUp"
        :value="selectedDocuments"
        @input="$emit('update:selectedDocuments', $event)"
        item-key="name"
      >
        <template slot="items" slot-scope="props">
          <tr :key="props.index" v-if="!hiddenItem(props.item)">
            <td class="hidden-sm-and-down" style="max-width: 10px">
              <v-checkbox
                v-model="props.selected"
                hide-details
                color="lightgrey"
              ></v-checkbox>
            </td>

            <td class="text-xs-left py-2 ph-no-capture">
              <v-layout align-center>
                <sx-document-icon
                  :is-empty="!Boolean(props.item.items_count)"
                  :is-folder="props.item.type === 'folder'"
                />
                <v-layout v-if="props.item.renameFile" align-center>
                  <v-text-field
                    :value="newName"
                    @input="$emit('update:newName', $event)"
                    hide-details
                    solo
                    class="default-input mx-2"
                  />
                  <span
                    v-if="props.item.type === 'file'"
                    class="black--text subheading"
                    >.{{ documentExtension }}</span
                  >
                  <v-tooltip bottom class="mx-1 pt-1">
                    <i
                      class="material-icons-outlined success--text"
                      style="font-size: 20px; cursor: pointer"
                      @click="$emit('renameItem')"
                      slot="activator"
                    >
                      check
                    </i>
                    <span>Save</span>
                  </v-tooltip>

                  <v-tooltip bottom class="ml-1 pt-1">
                    <i
                      class="material-icons-outlined error--text"
                      style="font-size: 20px; cursor: pointer"
                      @click="$emit('cancelRenaming', props.item)"
                      slot="activator"
                    >
                      cancel
                    </i>
                    <span>{{ $t('cancel') }}</span>
                  </v-tooltip>
                </v-layout>

                <v-layout
                  v-else
                  class="ml-1"
                  style="cursor: pointer"
                  column
                  :class="
                    props.item.type === 'folder' &&
                      parseInt(props.item.items_count) === 0 &&
                      'documents-folder--empty'
                  "
                  @click="
                    props.item.type === 'folder'
                      ? $emit('goToFolder', props.item.name)
                      : $emit('previewDocument', props.item)
                  "
                >
                  <TextHighlight
                    v-if="!isEmpty(searchingTerm)"
                    class="ph-no-capture"
                    :text="props.item.name"
                    :query="searchingTerm"
                  ></TextHighlight>
                  <span v-else class="ph-no-capture">{{
                    props.item.name
                  }}</span>

                  <span
                    v-if="props.item.path"
                    class="caption grey--text text--darken-3"
                  >
                    In {{ props.item.path }}
                  </span>

                  <span class="caption grey--text text--darken-1">
                    {{ getItemDetails(props.item) }}
                  </span>
                </v-layout>
              </v-layout>
            </td>

            <td class="text-xs-left">
              <v-layout
                column
                v-if="props.item.editingDescription"
                align-center
              >
                <v-textarea
                  v-model="props.item.newDescription"
                  hide-details
                  solo
                  class="default-input mt-2"
                  rows="3"
                />

                <v-layout>
                  <v-btn
                    @click="$emit('saveNewDescription', props.item)"
                    color="primary"
                    small
                    class="text-none"
                  >
                    Save
                  </v-btn>

                  <v-btn
                    @click="$emit('cancelEditingDescription', props.item)"
                    color="grey"
                    small
                    outline
                    class="text-none"
                  >
                    Cancel
                  </v-btn>
                </v-layout>
              </v-layout>

              <span v-else>
                <span>{{
                  !isEmpty(props.item.description)
                    ? props.item.description
                    : '-'
                }}</span>

                <v-icon
                  v-if="props.item.type !== 'folder'"
                  class="edit-icon"
                  small
                  color="primary"
                  @click="$emit('startEditingDescription', props.item)"
                >
                  edit
                </v-icon>
              </span>
            </td>

            <td class="text-xs-left">
              <v-layout>
                <v-chip
                  label
                  outline
                  small
                  color="#545454"
                  :key="i"
                  v-for="(tag, i) in props.item.tags"
                >
                  {{ tag }}
                </v-chip>
              </v-layout>
            </td>

            <td class="text-xs-center">
              <v-layout align-center v-if="!!props.item.updated_at">
                <span class="documents-row__updated-date">{{
                  moment(props.item.updated_at).format('lll')
                }}</span>
              </v-layout>
              <v-layout align-center v-else>
                <span class="documents-row__updated-date">-</span>
              </v-layout>
            </td>
            <td class="text-xs-center">
              <v-layout align-center v-if="!!props.item.created_at">
                <span class="documents-row__updated-date">{{
                  moment(props.item.created_at).format('lll')
                }}</span>
              </v-layout>
              <v-layout align-center v-else>
                <span class="documents-row__updated-date">-</span>
              </v-layout>
            </td>

            <td>
              <v-layout
                align-center
                justify-center
                v-if="
                  !disableFolderAction ||
                    isAdvisor ||
                    props.item.type === 'file'
                "
              >
                <v-menu
                  transition="slide-y-transition"
                  bottom
                  right
                  class="my-0"
                >
                  <i
                    slot="activator"
                    class="material-icons-outlined darkgrey--text"
                    :disabled="props.item.renameFile || loading"
                  >
                    more_vert
                  </i>
                  <v-list class="caption">
                    <v-list-tile @click="$emit('startRenaming', props.item)">
                      <v-list-tile-title> Rename </v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile
                      @click="$emit('openEditTags', props.item)"
                      v-if="props.item.type !== 'folder'"
                    >
                      <v-list-tile-title> Edit Tags </v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile @click="$emit('openMoveDialog', props.item)">
                      <v-list-tile-title> Move </v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile @click="$emit('openCopyDialog', props.item)">
                      <v-list-tile-title> Copy </v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile
                      @click="
                        $emit('openAttachToQuestionnaireDialog', props.item)
                      "
                      v-if="
                        props.item.type !== 'folder' &&
                          isAdvisor &&
                          !props.item.questionnaire_id
                      "
                    >
                      <v-list-tile-title>
                        Attach to Questionnaire
                      </v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile
                      @click="$emit('openDeleteConfirmationDialog', props.item)"
                    >
                      <v-list-tile-title> Delete </v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile
                      @click="$emit('downloadFolder', props.item)"
                      v-if="props.item.type === 'folder' && isAdvisor"
                    >
                      <v-list-tile-title> Download </v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile
                      @click="$emit('downloadDocument', props.item, false)"
                      v-else-if="props.item.type !== 'folder'"
                    >
                      <v-list-tile-title> Download </v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile
                      @click="$emit('downloadDocument', props.item, true)"
                      v-if="
                        props.item.type !== 'folder' &&
                          isAdvisor &&
                          !isEmpty(props.item.annotations)
                      "
                    >
                      <v-list-tile-title>
                        Download with Annotations
                      </v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>
              </v-layout>
            </td>
          </tr>
        </template>
        <template slot="no-data">
          <v-layout class="text-xs-center grey--text">
            <v-flex>
              <v-icon size="16" color="grey"> priority_high </v-icon>
              <span v-if="!isEmpty(searchingTerm)" class="ml-1"
                >No results found.</span
              >
              <span v-else class="ml-1">Empty Folder</span>
            </v-flex>
          </v-layout>
        </template>
      </v-data-table>
    </v-layout>
  </v-container>
</template>

<script>
import GlobalUiAlert from '@/components/GlobalUiAlert';
import { mapState, mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import upperFirst from 'lodash/upperFirst';
import moment from 'moment';
import FirmPreferenceMixins from '../mixins/FirmPreferenceMixins';
import TextHighlight from '@/components/TextHighlight';
import SxDocumentIcon from '@/components/sx/SxDocumentIcon';

export default {
  components: {
    GlobalUiAlert,
    TextHighlight,
    SxDocumentIcon,
  },

  mixins: [FirmPreferenceMixins],

  props: {
    loading: {
      type: Boolean,
    },

    paths: {
      type: Array,
    },

    documents: {
      type: Array,
    },

    selectedDocuments: {
      type: Array,
    },

    customDocumentFilter: {
      type: Function,
    },

    // For renaming
    documentExtension: {
      type: String,
    },

    newName: {
      type: String,
    },

    searchingTerm: {
      type: String,
    },

    searchTerm: {
      type: String,
    },

    clientQuestionnaires: {
      type: Array,
    },
  },

  data() {
    return {
      documentHeaders: [
        {
          text: 'Name',
          align: 'left',
          sortable: true,
          value: 'name',
          width: '25%',
        },
        {
          text: 'Description',
          align: 'left',
          sortable: true,
          value: 'description',
          width: '20%',
        },
        {
          text: 'Tags',
          align: 'left',
          sortable: true,
          value: 'tags',
          width: '20%',
        },
        {
          text: 'Last modified',
          align: 'left',
          sortable: true,
          value: 'updated_at',
          width: '15%',
        },
        {
          text: 'Uploaded at',
          align: 'left',
          sortable: true,
          value: 'created_at',
          width: '15%',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
          width: '5%',
        },
      ],
    };
  },

  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      isFirmCommunicationDisabled: 'isFirmCommunicationDisabled',
      isFirmDocumentsDisabled: 'isFirmDocumentsDisabled',
      currentUser: 'user',
      isAdvisor: 'isAdvisor',
      defaultCompany: 'defaultCompany',
    }),
    ...mapGetters(['currentFirm']),
    hasFolder() {
      return this.selectedDocuments.some(doc => doc.type === 'folder');
    },
    allAttached() {
      return this.selectedDocuments.every(doc => doc.questionnaire_id !== null);
    },
  },

  methods: {
    isEmpty,
    upperFirst,
    moment,

    clearSearch() {
      this.$emit('update:searchTerm', '');
      this.$emit('searchDocuments');
    },

    getItemDetails(item) {
      const details = [];
      if (item.name.includes('STAFF ONLY')) {
        details.push('Hidden from clients');
      }

      if (item.questionnaire_id) {
        const questionnaire = this.clientQuestionnaires.find(
          questionnaire => questionnaire.id == item.questionnaire_id,
        );
        if (questionnaire && questionnaire.name) {
          details.push('Attached to ' + questionnaire.name);
        }
      }

      return details.join(', ');
    },

    hiddenItem(item) {
      if (this.isAdvisor) {
        return item.type === 'folder' && item.name === '_soraban';
      } else {
        return (
          (item.type === 'folder' && item.name === '_soraban') ||
          item.name.includes('STAFF ONLY')
        );
      }
    },
  },
};
</script>

<style lang="postcss" scoped>
.breadcrumbs {
  padding: 8px 0px;
  font-size: 16px;

  & >>> .clickable {
    cursor: pointer;
    color: #777;
  }
  & >>> .clickable:hover {
    color: #000;
  }
}

.search-input {
  border: 1px solid #ccc;
  border-radius: 3px;

  & >>> .v-input__control {
    min-height: 36px;
  }

  & >>> .v-input__control .v-input__slot {
    background-color: transparent;
  }
}

.folder-icon {
}

.documents-table {
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  overflow: hidden;

  & >>> thead {
    background-color: #f4f6fa;
  }

  & >>> thead tr {
    height: 50px;
  }

  & >>> thead th.column {
    font-size: 15px;
    font-weight: bold;
    color: #666;
  }

  & >>> tbody td {
    font-size: 15px;
  }
}

.edit-icon {
  cursor: pointer;
}

.documents-folder--empty {
  color: var(--gray-500);
}

.documents-folder--empty > span:first-child:after {
  padding-left: 6px;

  color: var(--gray-200);

  content: ' (Empty)';
  font-style: italic;
}
</style>
